/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import Image from 'next/image'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'
import { getPreferredVideoSources, getVideoPosterSrc } from '@/lib/videoSources'
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from '@/components/shared/TurnstileWidget'
import { useLocale } from '../LocaleContext'

const STORAGE_KEY = 'gallium_contact_email'

const DEFAULT_VIDEO = '/HomePageVideo.mp4'

export function NewsletterSubscribeModal({
  isOpen,
  onClose,
  initialEmail = '',
  videoSrc = DEFAULT_VIDEO,
}: {
  isOpen: boolean
  onClose: () => void
  initialEmail?: string
  videoSrc?: string
}) {
  const videoSources = getPreferredVideoSources(videoSrc)
  const posterSrc = getVideoPosterSrc(videoSrc)
  const { locale, translatedData } = useLocale()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [submitBtnHovered, setSubmitBtnHovered] = useState(false)
  const turnstileRef = useRef<TurnstileWidgetHandle>(null)

  const inputBase =
    'w-full min-w-0 rounded-none border border-neutral-200 bg-neutral-100 px-3 py-2.5 font-poppins text-base font-normal tracking-normal text-[#1a1a1a] placeholder:font-bebas-neue placeholder:uppercase placeholder:text-neutral-500 focus:border-[#FF0000] focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:ring-offset-0 disabled:opacity-60 min-h-[44px] sm:px-4 sm:py-3 sm:text-[20.51px] sm:leading-[30.77px]'
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  /**
   * Tracks whether modal was closed
   * via browser/device back button
   */
  const closedByPopState = useRef(false)

  /**
   * Modal setup
   */
  useEffect(() => {
    if (!isOpen) return

    const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null

    setEmail(initialEmail || stored || '')
    setStatus('idle')
    setMessage(null)
    setTurnstileToken(null)
    setSubmitBtnHovered(false)
    turnstileRef.current?.reset()

    closeRef.current?.focus()
  }, [isOpen, initialEmail])

  /**
   * Prevent body scroll
   */
  useEffect(() => {
    if (!isOpen) return

    const scrollY = window.scrollY

    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.overflow = 'hidden'

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.overflow = ''
      window.scrollTo(0, Math.abs(parseInt(scrollY?.toString() ?? '0', 10)))
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  /**
   * Browser/device back button handling
   */
  useEffect(() => {
    if (!isOpen) return

    closedByPopState.current = false

    // Push fake history state
    window.history.pushState({ newsletterModal: true }, '')

    const handlePopState = () => {
      closedByPopState.current = true
      onClose()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isOpen, onClose])

  /**
   * Proper modal close
   * Handles X button / overlay click
   */
  const handleClose = () => {
    onClose()

    /**
     * If user did NOT use browser back,
     * remove fake history entry manually
     */
    if (!closedByPopState.current && window.history.state?.newsletterModal) {
      window.history.back()
    }
  }

  /**
   * Focus trap
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, [tabindex]:not([tabindex="-1"])',
    )

    const list = Array.from(focusables)

    const first = list[0]
    const last = list[list.length - 1]

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last?.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first?.focus()
    }
  }, [])

  /**
   * Form submit
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (status === 'loading') return

    const trimmed = email.trim()
    if (!trimmed) {
      setStatus('error')
      setMessage(translatedData?.common.email_required ?? 'Please enter your email address.')
      return
    }

    if (!turnstileToken) {
      setStatus('error')
      setMessage(
        translatedData?.common.turnstile_required ??
          'Please complete the security verification before submitting.',
      )
      return
    }

    setStatus('loading')

    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, turnstileToken }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setStatus('error')
        setTurnstileToken(null)
        turnstileRef.current?.reset()
        setMessage(data.error ?? translatedData?.common.generic_error ?? 'Something went wrong.')
        return
      }
      setStatus('success')
      setMessage(
        data.message ?? translatedData?.common.subscribe_success ?? 'Thank you for subscribing.',
      )
      setEmail('')

      if (typeof window !== 'undefined') localStorage.removeItem(STORAGE_KEY)
      setTurnstileToken(null)
      turnstileRef.current?.reset()
    } catch {
      setStatus('error')
      setTurnstileToken(null)
      turnstileRef.current?.reset()
      setMessage(translatedData?.common.generic_error ?? 'Something went wrong.')
    }
  }

  if (!isOpen) return null

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={translatedData?.common.subscribe_to_newsletter ?? 'Subscribe to newsletter'}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/80 backdrop-blur-md p-2 sm:p-4"
      onKeyDown={handleKeyDown}
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label={translatedData?.common.close_menu ?? 'Close'}
        className="absolute inset-0 cursor-default"
        onClick={handleClose}
        tabIndex={-1}
      />

      <div className="relative z-30 flex h-[calc(100dvh-1rem)] max-h-[680px] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-none bg-white shadow-xl sm:h-[calc(100dvh-2rem)] sm:max-w-[1025px] sm:flex-row">
        {/* Close button */}
        <button
          ref={closeRef}
          type="button"
          aria-label={translatedData?.common.close_menu ?? 'Close'}
          onClick={handleClose}
          className="absolute right-3 top-3 rtl:right-[unset] rtl:left-3 z-50 text-white hover:opacity-70 sm:right-4 sm:top-4 sm:text-black"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Video */}
        <div className="relative h-[clamp(7rem,22dvh,11rem)] w-full shrink-0 bg-neutral-900 sm:h-full sm:w-[45%] sm:min-h-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={posterSrc}
            className="h-full min-h-0 w-full object-cover"
            aria-hidden
          >
            {videoSources.map((source) => (
              <source key={source.src} src={source.src} type={source.type} />
            ))}
          </video>

          <div className="absolute inset-0 flex items-center justify-center">
            <Image
              src="/logo.svg"
              alt="Gallium"
              width={280}
              height={70}
              className="w-[140px] object-contain sm:w-[220px] lg:w-[280px]"
              priority
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative flex flex-1 min-h-0 min-w-0 flex-col justify-start overflow-hidden px-4 pt-6 pb-4 sm:justify-center sm:px-8 sm:pt-12 sm:pb-6 lg:pt-16">
          <h2 className="font-bebas-neue mb-2 text-3xl uppercase leading-tight text-black sm:mb-3 sm:text-[48px] sm:leading-[100%] lg:mb-6">
            {translatedData?.common.subscribe_us_title ?? 'SUBSCRIBE US!'}
          </h2>
          <p className="mb-2 font-poppins text-sm leading-snug text-neutral-800 sm:mb-3 sm:text-base sm:leading-[26.4px] lg:mb-6">
            {translatedData?.common.newsletter_blurb ??
              "To stay updated on our latest products, news and trends. Don't worry, we won't spam you!"}
          </p>
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-2.5 sm:gap-3">
            <label
              htmlFor="newsletter-email"
              className="font-poppins font-semibold uppercase"
              style={{
                color: '#545454',
                fontSize: 14,
              }}
            >
              {translatedData?.common.email_address ?? 'Email address'}
            </label>
            <input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => {
                const v = e.target.value
                setEmail(v)
                if (message) {
                  setMessage(null)
                  setStatus('idle')
                  setTurnstileToken(null)
                  turnstileRef.current?.reset()
                }
                if (typeof window !== 'undefined' && v.trim()) {
                  localStorage.setItem(STORAGE_KEY, v.trim())
                }
              }}
              placeholder={translatedData?.common.email_address ?? 'Email address'}
              disabled={status === 'loading'}
              className={inputBase}
            />
            <div className={message ? undefined : 'min-h-0'} aria-live="polite">
              {message ? (
                <p
                  className={`text-sm ${status === 'success' ? 'text-green-600' : 'text-red-600'}`}
                  role="alert"
                >
                  {message}
                </p>
              ) : null}
            </div>
            <TurnstileWidget
              ref={turnstileRef}
              active={isOpen && status === 'idle'}
              action="newsletter"
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              className="min-w-0 shrink-0"
            />
            <div className="newsletter-submit-btn-wrapper inline-flex w-full">
              <button
                type="submit"
                disabled={status === 'loading'}
                onMouseEnter={() => {
                  if (status !== 'loading') setSubmitBtnHovered(true)
                }}
                onMouseLeave={() => setSubmitBtnHovered(false)}
                className={`newsletter-submit-btn flex min-h-[44px] shrink-0 w-full items-center justify-center gap-2 rounded-none bg-[#E40520] py-4 px-6 font-poppins text-center text-sm font-normal leading-snug tracking-normal uppercase text-white focus:outline-none focus:ring-2 focus:ring-[#E40520] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-[10px] sm:py-[21px] sm:px-[33px] sm:text-[17.14px] sm:leading-[26.38px] ${submitBtnHovered ? 'newsletter-submit-btn--hovered' : ''}`}
              >
                {status === 'loading'
                  ? (translatedData?.common.subscribing ?? 'Subscribing...')
                  : (translatedData?.common.subscribe_button ?? 'SUBSCRIBE')}
                {locale == 'ar' ? (
                  <LuArrowUpLeft className="newsletter-submit-btn__arrow h-5 w-5 shrink-0" aria-hidden />
                ) : (
                  <LuArrowUpRight className="newsletter-submit-btn__arrow h-5 w-5 shrink-0" aria-hidden />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
